import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { trackButtonClick } from '../../utils/analytics'
import { validateUUID } from '../../utils/validators'
import { baseInputClasses, labelClasses, getTextareaClasses, getSelectClasses, helperTextClasses } from '../../styles/inputStyles'

/**
 * ContactFormPanel - Handles both authenticated and unauthenticated contact form submissions
 * For authenticated users: simplified message-only form
 * For unauthenticated users: full contact form with optional vendor fields
 */
function ContactFormPanel({
  isAuthenticated,
  contactSuccess,
  contactRateLimitTime,
  contactFormData,
  isVendor,
  isVendorLocked,
  sending,
  newMessage,
  onContactChange,
  onContactSubmit,
  onSendMessage,
  onSetIsVendor,
  onSetContactSuccess,
  onSetNewMessage,
  onTabChange
}) {
  const { t } = useTranslation()

  if (!isAuthenticated) {
    // Full contact form for non-authenticated users
    return (
      <div className="flex-1 min-h-0 overflow-y-auto py-4 px-4">
        <div className="max-w-md mx-auto">
          {contactSuccess ? (
            <ContactSuccessMessage
              isAuthenticated={isAuthenticated}
              contactRateLimitTime={contactRateLimitTime}
              onTabChange={onTabChange}
              onReset={() => onSetContactSuccess(false)}
            />
          ) : (
            <>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t('contact.title')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('contact.description')}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800">
                  <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                  {t('channels.contactForm.signInPrompt')}
                </p>
              </div>
              <form onSubmit={onContactSubmit} className="space-y-4">
                <BasicContactFields
                  formData={contactFormData}
                  onChange={onContactChange}
                />
                <VendorToggle isVendor={isVendor} onChange={onSetIsVendor} isLocked={isVendorLocked} />
                {isVendor && (
                  <VendorFields
                    formData={contactFormData}
                    onChange={onContactChange}
                    isEntityIdLocked={isVendorLocked && !!contactFormData.entityId}
                  />
                )}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full py-2 px-4 bg-primary text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? t('contact.sending') : t('contact.submit')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    )
  }

  // Simplified form for authenticated users
  return (
    <div className="flex-1 min-h-0 overflow-y-auto py-6 px-6">
      <div className="max-w-md mx-auto">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('channels.contactForm.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('channels.contactForm.subtitle')}
        </p>
        <p className="text-xs text-primary bg-primary-50 p-3 rounded mb-4">
          {t('channels.contactForm.signedInNotice')}
        </p>
        <form onSubmit={onSendMessage} className="space-y-4">
          <div>
            <label className={labelClasses}>{t('channels.contactForm.messageLabel')}</label>
            <textarea
              value={newMessage}
              onChange={(e) => onSetNewMessage(e.target.value)}
              placeholder={t('channels.contactForm.messagePlaceholder')}
              rows={6}
              disabled={sending}
              className={getTextareaClasses()}
            />
            {newMessage.trim().length > 0 && newMessage.trim().length < 10 && (
              <p className="text-xs text-gray-500 mt-1">
                {t('channels.message.charactersRemaining', { count: 10 - newMessage.trim().length })}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim() || newMessage.trim().length < 10 || sending}
            className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {sending ? t('channels.contactForm.sendingButton') : t('channels.contactForm.sendButton')}
          </button>
        </form>
      </div>
    </div>
  )
}

/**
 * Contact success message with signup CTA for unauthenticated users
 */
function ContactSuccessMessage({ isAuthenticated, contactRateLimitTime, onTabChange, onReset }) {
  const { t } = useTranslation()

  return (
    <div className="text-center space-y-4">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900">
        {t('contact.successTitle')}
      </h2>
      <p className="text-sm text-gray-600">
        {t('contact.successMessage')}
      </p>
      {!isAuthenticated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700 mb-3">
            {t('channels.contactForm.signUpCTA')}
          </p>
          <button
            onClick={() => {
              trackButtonClick('signup_cta', 'contact_tab', { source: 'contact_success' })
              onTabChange && onTabChange('auth')
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            {t('channels.contactForm.signUpButton')}
          </button>
        </div>
      )}
      {contactRateLimitTime && (
        <p className="text-xs text-gray-500">
          {t('channels.contactForm.rateLimitNotice')}
        </p>
      )}
      <button
        onClick={onReset}
        disabled={contactRateLimitTime && Date.now() < contactRateLimitTime}
        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t('contact.sendAnother')}
      </button>
    </div>
  )
}

/**
 * Basic contact form fields (name, email, subject, message)
 */
function BasicContactFields({ formData, onChange }) {
  const { t } = useTranslation()

  return (
    <>
      <div>
        <label className={labelClasses}>
          {t('contact.name')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          required
          className={baseInputClasses}
          placeholder={t('contact.namePlaceholder')}
        />
      </div>
      <div>
        <label className={labelClasses}>
          {t('contact.email')} <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onChange}
          required
          className={baseInputClasses}
          placeholder={t('contact.emailPlaceholder')}
        />
      </div>
      <div>
        <label className={labelClasses}>
          {t('contact.subject')}
        </label>
        <input
          type="text"
          name="subject"
          value={formData.subject}
          onChange={onChange}
          className={baseInputClasses}
          placeholder={t('contact.subjectPlaceholder')}
        />
      </div>
      <div>
        <label className={labelClasses}>
          {t('contact.message')} <span className="text-red-500">*</span>
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={onChange}
          required
          rows={4}
          className={getTextareaClasses()}
          placeholder={t('contact.messagePlaceholder')}
        />
      </div>
    </>
  )
}

/**
 * Vendor checkbox toggle
 */
function VendorToggle({ isVendor, onChange, isLocked }) {
  const { t } = useTranslation()

  return (
    <div className="border-t pt-3">
      <div className="flex items-start">
        <input
          type="checkbox"
          checked={isVendor}
          onChange={(e) => !isLocked && onChange(e.target.checked)}
          disabled={isLocked}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
        />
        <label className="ml-2 block text-sm font-medium text-gray-700">
          {t('contact.isVendor')}
          {isLocked && <span className="ml-1 text-xs text-primary font-normal">✓ {t('vendor.claim.cta', 'Become a steward')}</span>}
        </label>
      </div>
    </div>
  )
}

/**
 * Vendor-specific fields (intent, entity name, entity ID)
 */
function VendorFields({ formData, onChange, isEntityIdLocked }) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3 bg-gray-50/90 backdrop-blur-sm p-4 rounded-lg border border-gray-200/50 shadow-sm">
      <h4 className="text-base font-semibold text-gray-900">
        {t('contact.vendorInfo')}
      </h4>
      <div>
        <label className={labelClasses}>
          {t('contact.vendorIntent')} <span className="text-red-500">*</span>
        </label>
        <select
          name="vendorIntent"
          value={formData.vendorIntent}
          onChange={onChange}
          required
          className={getSelectClasses()}
        >
          <option value="">{t('contact.selectIntent')}</option>
          <option value="add">{t('contact.intents.add')}</option>
          <option value="update">{t('contact.intents.update')}</option>
          <option value="claim">{t('contact.intents.claim')}</option>
          <option value="confirm">{t('contact.intents.confirm')}</option>
          <option value="delete">{t('contact.intents.delete')}</option>
          <option value="general">{t('contact.intents.general')}</option>
        </select>
      </div>
      <div>
        <label className={labelClasses}>
          {t('contact.entityName')}
          {isEntityIdLocked && <span className="ml-1 text-xs text-gray-400">({t('contact.prefilled', 'pre-filled')})</span>}
        </label>
        <input
          type="text"
          name="entityName"
          value={formData.entityName}
          onChange={onChange}
          disabled={isEntityIdLocked}
          className={`${baseInputClasses} ${isEntityIdLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
          placeholder={t('contact.entityNamePlaceholder')}
        />
      </div>
      <div>
        <label className={labelClasses}>
          {t('contact.entityId')}
          {isEntityIdLocked && <span className="ml-1 text-xs text-gray-400">({t('contact.prefilled', 'pre-filled')})</span>}
        </label>
        <input
          type="text"
          name="entityId"
          value={formData.entityId}
          onChange={onChange}
          disabled={isEntityIdLocked}
          className={`${baseInputClasses} ${isEntityIdLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
          placeholder={t('contact.entityIdPlaceholder')}
        />
        <p className={helperTextClasses}>
          {t('contact.entityIdHelp')}
        </p>
      </div>
    </div>
  )
}

export default memo(ContactFormPanel)